import { JSONPath } from "jsonpath-plus";
import uniqBy from "lodash.uniqby";

export const KEY_SEPARATOR = "::";
export const PSEUDO_KEY_EMPTY = "<empty />";

export function makeRootLocalKey(localKey) {
  if (typeof localKey !== "string" || localKey.length === 0) return null;
  if (localKey.startsWith(KEY_SEPARATOR)) return localKey;
  return KEY_SEPARATOR + localKey;
}

export function joinLocalKeys(...keys) {
  return keys.filter((k) => !!k).join(KEY_SEPARATOR);
}

export function wrapIconData({ name }) {
  return name ? `<icon name="${name}" />` : "";
}

export function unwrapIcon(wrapped) {
  const nameMatch = wrapped && wrapped.match(/name="(.*?)"/i);
  return { name: nameMatch ? nameMatch[1] : null };
}

function resolveDataInterpolation(attrStr, context) {
  const queryMatch = attrStr.match(/query="(.*?)"/i);
  return queryMatch ? JSONPath({ path: queryMatch[1], json: context }) : "";
}

export function formatTemplate(template, context = {}) {
  return template
    .replace(/<data(.*?) \/>/gi, (_, attrStr) =>
      resolveDataInterpolation(attrStr, context)
    )
    .trim();
}

function stripSeparator(key) {
  if (key.startsWith(KEY_SEPARATOR)) return key.slice(KEY_SEPARATOR.length);
  return key;
}

function resolveScopeChain(scopes) {
  if (scopes.length === 0) return [];
  if (scopes.length === 1)
    return scopes[0].filter((s) => !!s).map(stripSeparator);
  const scope = scopes[0];
  const remaining = scopes.slice(1);

  return uniqBy(
    scope.reduce((acc, key) => {
      if (typeof key !== "string" || key.length === 0)
        return [...acc, ...resolveScopeChain(remaining)];
      if (key.startsWith(KEY_SEPARATOR)) return [...acc, stripSeparator(key)];
      return [
        ...acc,
        ...resolveScopeChain(remaining).map((c) => c + KEY_SEPARATOR + key),
      ];
    }, [])
  );
}

function logTranslateWarning(keys) {
  console.warn(`Missing localization for any of ${JSON.stringify(keys)}`);
}

export function buildKeys(prefixes, key) {
  if (key.startsWith(KEY_SEPARATOR)) return [stripSeparator(key)];
  if (prefixes.length === 0) return [key];
  return prefixes.map((prefix) => {
    if (prefix.length === 0) return key;
    return prefix + KEY_SEPARATOR + key;
  });
}

export function makeScopedTranslate(strings, scopes, expose = {}, onMissingKey) {
  const resolve = (key, context, opts) => {
    const template = strings[key];
    if (!template) return null;
    const result = opts.skipInterpolation
      ? template
      : formatTemplate(template, context);
    if (!opts.skipPseudoKeys) {
      if (result === PSEUDO_KEY_EMPTY) return " ";
    }
    return result;
  };
  const prefixes = resolveScopeChain(scopes);

  const t = (key, context, opts = {}) => {
    if (!key) return "";
    if (!strings) return key;

    let finalPrefixes = prefixes;
    if (opts.additionalScopes)
      finalPrefixes = resolveScopeChain([...opts.additionalScopes, ...scopes]);

    const keys = opts.overrideKeys || buildKeys(finalPrefixes, key);

    let result = keys.reduce((acc, k) => {
      if (acc) return acc;
      return resolve(k, context, opts);
    }, null);

    if (result) return result;

    onMissingKey?.({ key, keys });
    logTranslateWarning(keys);
    return `[${key}]`;
  };

  return [t, { ...expose, prefixes, scopes }];
}

export function makeTranslate(strings, keyPrefix = "", alternateKeyPrefix) {
  const resolve = (key, context) => {
    const template = strings[key];
    if (!template) return null;
    return formatTemplate(template, context);
  };

  const logWarning = (key) => {
    let warnLog = `Missing localization for '${keyPrefix}${key}'`;
    if (alternateKeyPrefix)
      warnLog = warnLog.concat(` or '${alternateKeyPrefix}${key}'`);
    console.warn(warnLog);
  };

  return (key, context) => {
    if (!key) return "";
    if (!strings) return key;

    if (key.startsWith(KEY_SEPARATOR))
      return resolve(key.slice(2), context) || key;

    let result = resolve(keyPrefix.concat(key), context);
    if (result) return result;

    result =
      alternateKeyPrefix && resolve(alternateKeyPrefix.concat(key), context);
    if (result) return result;

    logWarning(key);
    return key;
  };
}
